import TaskStatusEnum from "@/enum/TaskStatus";
import mainServerClient from "@/services/http/mainServerClient";
import { get, post } from "@/services/http/platformServer";
import { WrapperList } from "@/utils/utilityTypes";

export interface AuditSkuDto {
    externalId: string;
    originalId: string;
    skuProperties: string;
    /** 现在后端这个值就是简称 */
    title: string;
    /** 供货商视角使用 */
    settlementPrice?: SettlementPrice;
    /** 代理商视角使用 */
    retailerSettlementPrice?: SettlementPrice;
    retailerShopId: number;
    auditRole: AuditRole;
}

export interface AuditProductDto {
    externalId: string;
    originalId: string;
    picture: string;
    /** 供货商视角使用 */
    settlementPrice?: SettlementPrice;
    /** 代理商视角使用 */
    retailerSettlementPrice?: SettlementPrice;
    skus: AuditSkuDto[];
    title: string;
    simpleTitle?: string;
    retailerShopId: number;
    auditRole: AuditRole;
}

export interface IAuditSkuDto extends AuditSkuDto {
    productOriginalId: string;
}

export interface SettlementPrice {
    id: number;
    price: number;
    productOriginalId: string;
    retailerShopId: number;
    skuOriginalId: string;
}

interface DropShippingAuditProductQueryProcess {
    errorMsg?: string;

    productDtos: AuditProductDto[];

    status: "COMPLETED" | "ERROR" | "RUNNING";

    taskId: string;
}

/** 查询对账商品信息 */
export function queryDropshippingAuditProduct(supplierShopId: number, retailerShopId: number, auditRole: AuditRole) {
    return post<AuditProductDto[]>(
        auditRole === AuditRole.RETAILER ? retailerShopId : supplierShopId,
        "/drop_shipping_audit/product/query",
        { supplierShopId, retailerShopId, auditRole },
    );
}

export interface UpdateSettlementAuditPriceParams {
    price: number;
    productOriginalId: string;
    retailerShopId: number;
    skuOriginalId?: string;
    supplierShopId: number;
}

export interface SettlementPrice {
    id: number;
    price: number;
    productOriginalId: string;
    retailerShopId: number;
    skuOriginalId: string;
}

/** 供货商对代理商设置对账结算金额 */
export function updateSupplierSettlementAuditPrice(
    supplierShopId: number,
    params: UpdateSettlementAuditPriceParams,
): Promise<SettlementPrice> {
    return post(supplierShopId, "/drop_shipping_audit/supplier_settlement_price", params);
}
/** 代理商对供货商设置对账结算金额 */
export function updateRetailerSettlementAuditPrice(
    retailerShopId: number,
    params: UpdateSettlementAuditPriceParams,
): Promise<SettlementPrice> {
    return post(retailerShopId, "/drop_shipping_audit/retailer_settlement_price", params);
}

/** 供货商对代理商批量设置对账结算金额 */
export function batchUpdateSupplierSettlementAuditPrice(
    supplierShopId: number,
    params: UpdateSettlementAuditPriceParams[],
): Promise<SettlementPrice[]> {
    return post(supplierShopId, "/drop_shipping_audit/supplier_settlement_price_batch", params);
}

/** 代理商对供货商批量设置对账结算金额 */
export function batchUpdateRetailerSettlementAuditPrice(
    retailerShopId: number,
    params: UpdateSettlementAuditPriceParams[],
): Promise<SettlementPrice[]> {
    return post(retailerShopId, "/drop_shipping_audit/retailer_settlement_price_batch", params);
}

export enum DropShippingAuditTimeType {
    ORDER_SHIPPED = "ORDER_SHIPPED",

    ORDER_TIME = "ORDER_TIME",
}

export enum AuditRole {
    RETAILER = "RETAILER",

    SUPPLIER = "SUPPLIER",
}

export enum DropShippingAuditProductStatus {
    PENDING_SHIPMENT_WITH_REFUNDING = "PENDING_SHIPMENT_WITH_REFUNDING",
    PENDING_SHIPMENT_WITHOUT_REFUNDING = "PENDING_SHIPMENT_WITHOUT_REFUNDING",
    SHIPPED_WITH_CANCELED = "SHIPPED_WITH_CANCELED",
    SHIPPED_WITH_REFUNDING = "SHIPPED_WITH_REFUNDING",
    SHIPPED_WITHOUT_REFUNDING = "SHIPPED_WITHOUT_REFUNDING",
}

export const DropShippingAuditProductStatusNameMap = {
    [DropShippingAuditProductStatus.PENDING_SHIPMENT_WITHOUT_REFUNDING]: "待发货",
    [DropShippingAuditProductStatus.PENDING_SHIPMENT_WITH_REFUNDING]: "待发货退款中商品",
    [DropShippingAuditProductStatus.SHIPPED_WITHOUT_REFUNDING]: "已发货",
    [DropShippingAuditProductStatus.SHIPPED_WITH_REFUNDING]: "已发货退款中商品",
    [DropShippingAuditProductStatus.SHIPPED_WITH_CANCELED]: "已发货退款完成商品",
};

export enum AuditType {
    /** 仅商品对账 */
    PRODUCT_ONLY = "PRODUCT_ONLY",
    /** 商品及运费对账 */
    PRODUCT_AND_FREIGHT = "PRODUCT_AND_FREIGHT",
    /** 运费对账 */
    FREIGHT_ONLY = "FREIGHT_ONLY",
}

export const AuditTypeNameMap = {
    [AuditType.PRODUCT_ONLY]: "仅商品对账",
    [AuditType.PRODUCT_AND_FREIGHT]: "商品及运费对账",
    [AuditType.FREIGHT_ONLY]: "运费对账",
};

export interface CreateDropShippingAuditTaskParams {
    /**
     * 结算类型
     */
    auditType?: AuditType;
    /**
     * 对账发起的角色
     */
    auditRole: AuditRole;
    /**
     * 结算结束时间
     */
    end: string;
    /**
     * 结算商品状态
     */
    productStatuses: DropShippingAuditProductStatus[];
    /**
     * 分销商店铺ID
     */
    retailerShopId: number;
    /**
     * 结算开始时间
     */
    start: string;
    /**
     * 供货商店铺ID
     */
    supplierShopId: number;
    /**
     * 结算时间类型
     */
    timeType: DropShippingAuditTimeType;
}

/**
 * 开始对账
 *  */
export function createDropShippingAuditTask(params: CreateDropShippingAuditTaskParams): Promise<void> {
    return mainServerClient.post("/drop_shipping_audit", params);
}

export interface BatchDropShippingAuditTaskParams {
    /**
     * 结算类型
     */
    auditType: AuditType;
    /**
     * 对账发起角色
     */
    auditRole: AuditRole;
    /**
     * 结算结束时间
     */
    end: string;
    /**
     * 结算商品状态
     */
    productStatuses: DropShippingAuditProductStatus[];
    /**
     * 结算开始时间
     */
    start: string;
    /**
     * 供应商-代理商对列表
     */
    supplierRetailerPairs: { retailerShopId: number; supplierShopId: number }[];
    /**
     * 结算时间类型
     */
    timeType: DropShippingAuditTimeType;
}

/**
 * 批量对账
 */
export function batchCreateDropShippingAuditTask(params: BatchDropShippingAuditTaskParams): Promise<void> {
    return mainServerClient.post("/drop_shipping_audit/batch", params);
}
export interface AuditListParams {
    pageNo: number;
    pageSize: number;
    retailerShopId: number;
    supplierShopId: number;
    auditRole: AuditRole;
}

export enum DropShippingAuditTaskType {
    BatchChild = "BATCH_CHILD",
    BatchParent = "BATCH_PARENT",
    Normal = "NORMAL",
}

export interface DropShippingAuditTask {
    /**
     * 对账结算总金额
     */
    amount?: number;
    auditRole?: AuditRole;
    auditType?: AuditType;
    childTasks?: DropShippingAuditTask[];
    companyId?: number;
    /**
     * 待对方确认
     */
    confirmed?: boolean;
    /**
     * 创建时间
     */
    dateCreated?: string;
    distributedTaskId?: number;
    /**
     * OSS 下载地址
     */
    downloadUrl?: string;
    errorMsg?: string;
    /**
     * 是否已被导出过，如果是的话，那么每次请求下载时直接去请求oss下载
     */
    hasBeenExported?: boolean;
    id?: number;
    /**
     * 最后更新时间
     */
    lastUpdated?: string;
    /**
     * 批量任务的父任务ID，仅当taskType为BATCH_CHILD时有值
     */
    parentTaskId?: number;
    /**
     * 结算商品状态
     */
    productStatuses?: DropShippingAuditProductStatus[];
    quantity?: number;
    /**
     * 对账相关备注信息
     */
    remark?: string;
    retailerShopId?: number;
    status?: TaskStatusEnum;
    supplierShopId?: number;
    /**
     * 任务类型
     */
    taskType?: DropShippingAuditTaskType;
    /**
     * 结算结束时间
     */
    timeEnd?: string;
    /**
     * 结算开始时间
     */
    timeStart?: string;
    /**
     * 结算时间类型
     */
    timeType?: DropShippingAuditTimeType;
    uuid?: string;

    childTaskRetailerShopIds?: number[];
    childTaskSupplierShopIds?: number;
}
/** 供货商查询代理商的对账账单 */
export function getRetailerAuditList(params: AuditListParams): Promise<WrapperList<DropShippingAuditTask>> {
    return mainServerClient.get("/drop_shipping_audit/list_retailer", params);
}

/** 代理商查询供货商的对账账单 */
export function getSupplierAuditList(params: AuditListParams): Promise<WrapperList<DropShippingAuditTask>> {
    return mainServerClient.get("/drop_shipping_audit/list_supplier", params);
}

interface ConfirmedAuditTaskParams {
    retailerShopId: number;
    supplierShopId: number;
    taskId: number;
}

/** 代理商确认对账 */
export function retailerAuditConfirm(params: ConfirmedAuditTaskParams): Promise<DropShippingAuditTask> {
    return mainServerClient.post("/drop_shipping_audit/retailer_audit_confirmed", params);
}

/** 供货商确认对账 */
export function supplierAuditConfirm(params: ConfirmedAuditTaskParams): Promise<DropShippingAuditTask> {
    return mainServerClient.post("/drop_shipping_audit/supplier_audit_confirmed", params);
}

/** 判断供货商是否已对代理商设置过结算价格 */
export function getSupplierSettlementPriceExists(supplierShopId: number, retailerShopId: number): Promise<boolean> {
    return get(supplierShopId, "/drop_shipping_audit/supplier_settlement_price/exists", {
        supplierShopId,
        retailerShopId,
    });
}

/** 判断代理商是否已对供货商设置过结算价格 */
export function getRetailerSettlementPriceExists(supplierShopId: number, retailerShopId: number): Promise<boolean> {
    return get(retailerShopId, "/drop_shipping_audit/retailer_settlement_price/exists", {
        supplierShopId,
        retailerShopId,
    });
}

/** 代理商导出分销对账单，并返回下载链接 */
export function retailerExportAudit(taskId: number): Promise<string> {
    return mainServerClient.get("/drop_shipping_audit/retailer_export", { taskId });
}

/** 供货商导出分销对账单，并返回下载链接 */
export function supplierExportAudit(taskId: number): Promise<string> {
    return mainServerClient.get("/drop_shipping_audit/supplier_export", { taskId });
}
